import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Search } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { AdminOrgRow } from '@repo/shared-types';
import { formatContactPhone } from '@repo/validation';
import { Input } from '@repo/ui/src/components/ui/input';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { DataTable, type DataTableColumnMeta } from '../components/DataTable';
import { PlanBadge } from '../components/PlanBadge';
import { PeriodEndCell } from '../components/PeriodEndCell';
import { useAdminOrgs } from '../hooks/useAdminOrgs';
import { formatDate } from '../lib/adminFormat';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 50;
const DEFAULT_SORT = 'createdAt:desc';

const columns: ColumnDef<AdminOrgRow, unknown>[] = [
  {
    id: 'name',
    header: 'Tên',
    meta: { sortField: 'name' } satisfies DataTableColumnMeta,
    cell: ({ row }) => (
      <Link to={`/orgs/${row.original.id}`} className="font-medium text-surface-900 hover:underline">
        {row.original.name}
      </Link>
    ),
  },
  {
    id: 'plan',
    header: 'Gói',
    cell: ({ row }) => <PlanBadge planCode={row.original.planCode} />,
  },
  {
    id: 'status',
    header: 'Trạng thái',
    cell: ({ row }) =>
      row.original.suspendedAt ? (
        <Badge variant="destructive">Đã khoá</Badge>
      ) : (
        <span className="text-surface-600">{row.original.subStatus ?? '—'}</span>
      ),
  },
  {
    id: 'currentPeriodEnd',
    header: 'Hạn',
    cell: ({ row }) => <PeriodEndCell currentPeriodEnd={row.original.currentPeriodEnd} />,
  },
  {
    id: 'studentCount',
    header: 'Học viên',
    meta: { headerClassName: 'text-right', cellClassName: 'text-right tabular-nums' } satisfies DataTableColumnMeta,
    cell: ({ row }) => row.original.studentCount,
  },
  {
    id: 'memberCount',
    header: 'Thành viên',
    meta: { headerClassName: 'text-right', cellClassName: 'text-right tabular-nums' } satisfies DataTableColumnMeta,
    cell: ({ row }) => row.original.memberCount,
  },
  {
    // The outreach gap in one glance: registration is email-only, so an org
    // with no number here can only be reached by mail.
    id: 'contactPhone',
    header: 'Zalo',
    meta: { cellClassName: 'tabular-nums' } satisfies DataTableColumnMeta,
    cell: ({ row }) =>
      row.original.contactPhone ? (
        <span className="text-surface-900">{formatContactPhone(row.original.contactPhone)}</span>
      ) : (
        <span className="text-surface-400">—</span>
      ),
  },
  {
    id: 'createdAt',
    header: 'Ngày tạo',
    meta: { sortField: 'createdAt', cellClassName: 'tabular-nums text-surface-600' } satisfies DataTableColumnMeta,
    cell: ({ row }) => formatDate(row.original.createdAt),
  },
];

export function OrgsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const q = searchParams.get('q') ?? '';

  const [searchInput, setSearchInput] = useState(q);

  // Keep the box in sync when `q` changes from outside a keystroke here (e.g.
  // browser Back/Forward, or a fresh deep link) — otherwise the box can show
  // stale text that no longer matches what the table is actually filtered by.
  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  // Debounce search input → URL (resets to page 1 on a real change only).
  // Guarding on `searchInput === q` makes this a no-op both on mount and
  // right after the sync effect above mirrors a URL-driven `q` into the box —
  // so the two effects can't ping-pong into a render loop, and a deep link's
  // `page` is never silently rewritten to 1.
  useEffect(() => {
    if (searchInput === q) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (searchInput) next.set('q', searchInput);
      else next.delete('q');
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, q]);

  const params = useMemo(() => ({ page, limit, sort, q: q || undefined }), [page, limit, sort, q]);
  const { data, isLoading, error } = useAdminOrgs(params);

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };

  const setSort = (nextSort: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', nextSort);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-surface-900">Tổ chức</h1>
        <p className="text-sm text-muted-foreground mt-1">Danh sách các tổ chức trên nền tảng.</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Tìm theo tên hoặc slug..."
          className="pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        page={page}
        limit={limit}
        sort={sort}
        onPageChange={setPage}
        onSortChange={setSort}
        isLoading={isLoading}
        error={error}
        emptyText={q ? 'Không tìm thấy tổ chức phù hợp.' : 'Chưa có tổ chức nào.'}
      />
    </div>
  );
}
